'use client'

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useRouter } from 'next/navigation'
import { useAppStore } from '@entities/app-store'
import { useFederalDistricts } from '@entities/federal-district'
import { fulfillmentApi, toOwnerFulfillment, useServiceOptions, type FulfillmentApi } from '@entities/fulfillment'
import { yupResolver } from '@hookform/resolvers/yup'
import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Button } from '@ui/base/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/base/card'
import { WizardFooter } from '@ui/wizard-footer'
import { cn } from '@utils/cn'
import { typeClass } from '@utils/typography'
import axios from 'axios'
import { observer } from 'mobx-react-lite'
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form'

import {
  draftToFulfillmentFormValues,
  emptyFulfillmentFormValues,
  normalizeFulfillmentFormDistricts,
  ownerToFulfillmentFormValues,
  toFulfillmentSaveRequest,
} from '../lib/form-mappers'
import {
  clearDraft,
  keepDraft,
  loadDraftFields,
  loadDraftFile,
  movePreviousDraft,
  takeDraftPhotoLost,
} from '../lib/fulfillment-draft'
import { deleteFulfillmentPhoto, uploadFulfillmentPhotos } from '../lib/photo-api'
import { rebuildServicePrices } from '../lib/service-prices-form'
import { useDraftAutosave, type DraftSaveState } from '../lib/use-draft-autosave'
import { fulfillmentFormSchema } from '../model/schema'
import { fieldsOfStep, formSteps, stepOfField } from '../model/steps'
import { type DraftData, type FulfillmentFormValues } from '../model/types'
import { ContactsStep } from './contacts-step'
import { coverIsAllowed } from './cover-field'
import { FulfillmentCreatedDialog } from './fulfillment-created-dialog'
import { GoodsStep } from './goods-step'
import { OrganizationStep } from './organization-step'
import { maxPhotoCount, photoIsAllowed } from './photos-field'
import { PresentationStep } from './presentation-step'
import { PriceStep } from './price-step'
import { ShipmentStep } from './shipment-step'

type Props = { mode: 'add' | 'edit'; slug?: string }

const draftRestoredNotice = 'Черновик восстановлен'
const draftPhotoLostNotice = 'Черновик восстановлен. Фото нужно выбрать ещё раз'
const draftFailedNotice = 'Браузер не дал сохранить черновик. Проверьте, разрешены ли данные сайта'
const districtSaveBlockedError =
  'Сохранение недоступно, пока не загрузились федеральные округа. Дождитесь загрузки или повторите её на шаге отгрузки.'
const districtReselectionError = 'Выберите федеральные округа заново на шаге отгрузки.'

/** Сколько зелёная плашка держится на экране: она сообщает о сделанном, а не о состоянии формы. */
const noticeLifetimeMs = 6000

/** Подпись главной кнопки: на последнем шаге она сохраняет карточку, на остальных — листает. */
const nextButtonLabel = (isLastStep: boolean, isSubmitting: boolean, mode: Props['mode']) => {
  if (!isLastStep) {
    return 'Далее'
  }
  if (isSubmitting) {
    return 'Сохраняем…'
  }

  return mode === 'add' ? 'Добавить фулфилмент' : 'Сохранить изменения'
}

/**
 * Сервер отвечает одним текстом на всю форму. Если в тексте узнаётся конкретное поле, повторяем
 * ответ у самого поля — иначе непонятно, что менять. Ключ — слово, по которому узнаём поле;
 * регистр и пробел в названии сервиса сервер пишет по-разному («2ГИС», «2Гис», «2 ГИС»).
 */
const fieldByServerMessage: [RegExp, 'inn' | 'twoGisUrl'][] = [
  [/инн/i, 'inn'],
  [/2\s?гис/i, 'twoGisUrl'],
]

const apiErrorText = (error: unknown) => {
  if (!axios.isAxiosError(error) || typeof error.response?.data !== 'object' || error.response.data === null) {
    return null
  }
  const message = (error.response.data as { errorText?: unknown }).errorText
  return typeof message === 'string' && message.trim() ? message : null
}

export const FulfillmentForm = observer(({ mode, slug }: Props) => {
  const router = useRouter()
  const appStore = useAppStore()
  const { districts: federalDistricts } = useFederalDistricts()
  const {
    services,
    isLoading: isServicesLoading,
    hasLoadError: servicesFailed,
    retry: retryServices,
  } = useServiceOptions()
  const form = useForm<FulfillmentFormValues>({
    mode: 'onBlur',
    resolver: yupResolver(fulfillmentFormSchema) as never,
    defaultValues: emptyFulfillmentFormValues,
  })
  const {
    handleSubmit,
    getValues,
    reset,
    setError,
    setValue,
    trigger,
    watch,
    formState: { errors, isSubmitting },
  } = form
  const [step, setStep] = useState(0)
  /** Шаг не пустил дальше: поля подсвечены, а плашка объясняет, почему кнопка не сработала. */
  const [wasStepRejected, setWasStepRejected] = useState(false)
  const [cover, setCover] = useState<File | null>(null)
  const [hasExistingCover, setHasExistingCover] = useState(false)
  const [coverError, setCoverError] = useState('')
  const [coverUploadFailed, setCoverUploadFailed] = useState(false)
  const [photoFiles, setPhotoFiles] = useState<File[]>([])
  const [savedPhotos, setSavedPhotos] = useState<FulfillmentApi.Photo[]>([])
  const [photoError, setPhotoError] = useState('')
  /** Номер показа отличает повторное сообщение с тем же текстом: иначе отсчёт не начался бы заново. */
  const [notice, setNotice] = useState<{ text: string; number: number } | null>(null)
  const [serverError, setServerError] = useState('')
  const [isCreatedDialogOpen, setIsCreatedDialogOpen] = useState(false)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'forbidden' | 'not-found' | 'error'>(
    mode === 'edit' ? 'loading' : 'ready'
  )
  const [savedId, setSavedId] = useState<string | null>(null)
  /** Пока черновик не подхвачен, автосохранению писать нечего: оно перетёрло бы сохранённое. */
  const [isDraftRestored, setIsDraftRestored] = useState(mode !== 'add')
  const stepTitle = useRef<HTMLHeadingElement>(null)
  const wasDraftFailureShown = useRef(false)

  const draftAutosave = useDraftAutosave({
    isEnabled: mode === 'add',
    isRestored: isDraftRestored,
    step,
    cover,
    form,
  })

  const shipmentTypes = watch('shipmentTypes') ?? []
  const marketplaces = watch('marketplaces') ?? []

  const applyKnownDistricts = (values: FulfillmentFormValues) =>
    federalDistricts ? normalizeFulfillmentFormDistricts(values, federalDistricts) : values

  /** Переход на шаг: содержимое карточки сменилось целиком, поэтому фокус уходит на её заголовок. */
  const showStep = (next: number) => {
    setStep(next)
    stepTitle.current?.focus()
  }

  const showNotice = useCallback(
    (text: string) => setNotice((shown) => ({ text, number: (shown?.number ?? 0) + 1 })),
    []
  )

  /** Листание — новое действие, и сообщения о прошлом на нём уже ни о чём не говорят. */
  const forgetNotices = () => {
    setNotice(null)
    // Ошибку сохранения оставляем, пока в ней есть незакрытое дело — повторная загрузка фото.
    if (!coverUploadFailed) {
      setServerError('')
    }
  }

  useEffect(() => {
    if (!notice) {
      return
    }
    const hide = setTimeout(() => setNotice(null), noticeLifetimeMs)

    return () => clearTimeout(hide)
  }, [notice])

  // Правку делает страница, а не человек, поэтому форма от неё не считается изменённой: иначе
  // черновик завёлся бы у любого, кто просто открыл шаг отгрузки.
  useEffect(() => {
    if (!shipmentTypes.includes('fbs')) {
      setValue('marketplaces', [])
    }
  }, [setValue, shipmentTypes])

  // Прайс держим ровно по отмеченным схемам работы. Пока справочник услуг не пришёл, собирать
  // нечего: пустой прайс затёр бы уже введённые цены.
  useEffect(() => {
    if (!services?.length) {
      return
    }
    setValue('servicePrices', rebuildServicePrices(getValues('servicePrices'), shipmentTypes, services))
  }, [getValues, services, setValue, shipmentTypes])

  useEffect(() => {
    if (!federalDistricts) {
      return
    }
    const normalizedMarketplaces = normalizeFulfillmentFormDistricts(
      { ...getValues(), marketplaces },
      federalDistricts
    ).marketplaces
    const hasChanges = normalizedMarketplaces.some((item, index) => {
      const current = marketplaces[index]

      return (
        !current ||
        item.needsDistrictReselection !== current.needsDistrictReselection ||
        item.destinations.length !== current.destinations.length
      )
    })
    if (hasChanges) {
      setValue('marketplaces', normalizedMarketplaces)
    }
  }, [federalDistricts, getValues, marketplaces, setValue])

  // Об отказе хранилища говорим один раз: надпись в шапке остаётся, а плашка иначе мигала бы
  // на каждую правку поля.
  useEffect(() => {
    if (draftAutosave.state !== 'failed' || wasDraftFailureShown.current) {
      return
    }
    wasDraftFailureShown.current = true
    setServerError(draftFailedNotice)
  }, [draftAutosave.state])

  useEffect(() => {
    if (mode !== 'edit' || !slug || !appStore.isLoggedIn) {
      return
    }
    fulfillmentApi
      .owner(slug)
      .then((response) => {
        const item = toOwnerFulfillment(response.data.data!.fulfillment)
        reset(applyKnownDistricts(ownerToFulfillmentFormValues(item)))
        setHasExistingCover(item.coverFileId !== null)
        setSavedPhotos(response.data.data!.photos ?? [])
        setSavedId(item.id)
        setLoadState('ready')
      })
      .catch((error: unknown) => {
        const responseStatus = axios.isAxiosError(error) ? error.response?.status : undefined
        setLoadState(responseStatus === 403 ? 'forbidden' : responseStatus === 404 ? 'not-found' : 'error')
      })
  }, [appStore.isLoggedIn, mode, reset, slug])

  // Черновик подхватываем всегда: посетитель мог уйти со страницы и вернуться через несколько дней.
  useEffect(() => {
    if (mode !== 'add') {
      return
    }
    // Все ветки восстановления заканчиваются одинаково: сказать человеку, что вышло, и разрешить
    // автосохранение. Отдельный вызов на каждую ветку легко забыть — тогда черновик молча перестал
    // бы сохраняться.
    const finishRestoring = (notice?: string) => {
      if (notice) {
        showNotice(notice)
      }
      setIsDraftRestored(true)
    }

    movePreviousDraft()
    const saved = loadDraftFields<DraftData>()
    if (!saved) {
      finishRestoring()

      return
    }
    reset(applyKnownDistricts(draftToFulfillmentFormValues(saved.values)))
    // Шаг из черновика проверяем на число: испорченная запись иначе увела бы за границы формы.
    // Ставим его без перевода фокуса: страница только открылась, и забирать у неё фокус незачем.
    const savedStep = saved.step ?? 0
    setStep(Number.isInteger(savedStep) ? Math.min(Math.max(savedStep, 0), formSteps.length - 1) : 0)
    if (takeDraftPhotoLost()) {
      finishRestoring(draftPhotoLostNotice)

      return
    }

    let isCurrentPage = true
    Promise.resolve(loadDraftFile())
      .then((file) => {
        if (!isCurrentPage) {
          return
        }
        if (file) {
          setCover(file)
        }
        finishRestoring(draftRestoredNotice)
      })
      .catch(() => {
        if (isCurrentPage) {
          finishRestoring(draftPhotoLostNotice)
        }
      })

    return () => {
      isCurrentPage = false
    }
  }, [mode, reset, showNotice])

  const validateCover = (file: File | undefined) => {
    if (!file) {
      setCover(null)
      return
    }
    if (!coverIsAllowed(file)) {
      setCoverError('Выберите JPEG, PNG или WebP до 5 МБ')
      setCover(null)
      return
    }
    setCoverError('')
    setCover(file)
  }

  const uploadCover = async (id: string) => {
    if (!cover) {
      return
    }
    const body = new FormData()
    body.append('cover', cover)
    const response = await fetch(`/api/fulfillment/${id}/cover`, { method: 'POST', body })
    if (!response.ok) {
      throw new Error('cover upload failed')
    }
  }

  const choosePhotos = (files: File[]) => {
    const allowed = files.filter(photoIsAllowed)
    const freeSlots = maxPhotoCount - savedPhotos.length
    if (allowed.length < files.length) {
      setPhotoError('Часть файлов пропущена: подойдут JPEG, PNG или WebP до 5 МБ')
    } else if (allowed.length > freeSlots) {
      setPhotoError(`Всего можно добавить ${maxPhotoCount} фотографий`)
    } else {
      setPhotoError('')
    }
    setPhotoFiles(allowed.slice(0, freeSlots))
  }

  const removeSavedPhoto = async (photoId: string) => {
    if (!savedId) {
      return
    }
    try {
      setSavedPhotos(await deleteFulfillmentPhoto(savedId, photoId))
      setPhotoError('')
    } catch {
      setPhotoError('Не удалось убрать фотографию. Попробуйте ещё раз')
    }
  }

  /** Фотографии уходят после сохранения карточки: до этого у неё нет идентификатора. */
  const uploadPhotos = async (id: string) => {
    if (photoFiles.length === 0) {
      return
    }
    setSavedPhotos(await uploadFulfillmentPhotos(id, photoFiles))
    setPhotoFiles([])
  }

  /**
   * Карточка уже создана, значит черновику конца ждать незачем. Убираем его сразу, не дожидаясь
   * загрузки фото: если она не удастся и человек закроет вкладку, оставшийся черновик восстановился
   * бы без признака сохранённой карточки — и «Добавить фулфилмент» завело бы вторую такую же.
   * Автосохранение останавливаем первым: отложенная запись иначе вернула бы черновик обратно.
   *
   * Только в режиме добавления: черновик один на оба режима, и сохранение чужой карточки на правке
   * стёрло бы недописанную новую.
   */
  const forgetDraftAfterSave = async () => {
    if (mode !== 'add') {
      return
    }
    await draftAutosave.cancel()
    await clearDraft()
  }

  /**
   * Уводим в «Мои фулфилменты», а не в каталог: новая карточка ждёт проверки поддержкой и в
   * каталоге ещё не появится. На своей странице владелец видит её вместе с пометкой состояния.
   * Черновика к этому моменту уже нет: его убирают сразу после создания карточки.
   */
  const goToMyFulfillments = () => {
    router.push('/fulfillment/my')
    router.refresh()
  }

  /** Только что созданную карточку показываем в окне: на правке показывать нечего, там уводим сразу. */
  const finishSuccessfulSave = () => {
    setServerError('')
    setCoverUploadFailed(false)

    if (mode === 'add') {
      setIsCreatedDialogOpen(true)
      return
    }

    goToMyFulfillments()
  }

  const closeDialogAndGoToMyFulfillments = () => {
    setIsCreatedDialogOpen(false)
    goToMyFulfillments()
  }

  /**
   * Сохраняем всё введённое и уводим на регистрацию: без аккаунта карточку не за кем закрепить.
   * Если черновик не лёг в хранилище, со страницы не уходим: заполненная форма пропала бы совсем.
   */
  const keepDraftAndAskToLogin = async (values: FulfillmentFormValues) => {
    // Автосохранение не выключаем: при отказе хранилища человек остаётся на этой же странице, и
    // выключенное навсегда сохранение оставило бы его дальше заполнять форму вообще без записи.
    const { areFieldsKept } = await keepDraft(values, step, cover)
    if (!areFieldsKept) {
      setServerError(`${draftFailedNotice}. Войдите в соседней вкладке и вернитесь сюда`)

      return
    }
    window.location.href = `/auth/register?callbackUrl=${encodeURIComponent('/fulfillment/add')}`
  }

  /**
   * Право на добавление проверяет сервер: сразу после входа локальное состояние ещё считает
   * посетителя гостем, и проверка по нему уводила на регистрацию по кругу.
   */
  const submit = async (values: FulfillmentFormValues) => {
    setServerError('')
    setCoverUploadFailed(false)

    try {
      const request = toFulfillmentSaveRequest(values, federalDistricts)
      const response = savedId ? await fulfillmentApi.update(savedId, request) : await fulfillmentApi.create(request)
      const id = response.data.data!.fulfillment.id
      setSavedId(id)
      await forgetDraftAfterSave()
      try {
        await uploadCover(id)
        await uploadPhotos(id)
        finishSuccessfulSave()
      } catch {
        setCoverUploadFailed(true)
        setServerError('Данные сохранены, но фото не загрузилось. Повторите загрузку или продолжите без фото.')
      }
    } catch (error) {
      const responseStatus = axios.isAxiosError(error) ? error.response?.status : undefined
      if (responseStatus === 401) {
        await keepDraftAndAskToLogin(values)
      } else if (responseStatus === undefined || responseStatus >= 500) {
        setServerError(
          'Не удалось подтвердить сохранение. Проверьте карточку в разделе «Мои фулфилменты» и повторите попытку.'
        )
      } else {
        const message = apiErrorText(error) ?? 'Не удалось сохранить данные. Проверьте поля и повторите попытку.'
        setServerError(message)
        const failedField = fieldByServerMessage.find(([hint]) => hint.test(message))?.[1]
        if (failedField) {
          setError(failedField, { message })
          showStep(stepOfField(failedField))
        }
      }
    }
  }

  /** Ошибку могла найти проверка всей формы — тогда открываем самый ранний шаг с ошибкой. */
  const showFirstInvalidStep = (invalid: FieldErrors<FulfillmentFormValues>) => {
    const invalidSteps = Object.keys(invalid).map(stepOfField)
    if (invalidSteps.length > 0) {
      showStep(Math.min(...invalidSteps))
      setWasStepRejected(true)
    }
  }

  const validateAndSubmit = handleSubmit(submit, showFirstInvalidStep)

  /**
   * Пока идёт сохранение, второй отправки не бывает: карточки ещё нет ни в ответе, ни в
   * состоянии, поэтому два запроса подряд завели бы её дважды.
   */
  const sendForm = async (event?: FormEvent<HTMLFormElement>) => {
    // Отправку останавливаем сами: без этого браузер перезагрузил бы страницу и потерял заполненное.
    event?.preventDefault()
    if (isSubmitting) {
      return
    }
    const values = applyKnownDistricts(getValues())
    const needsDistrictReselection =
      values.shipmentTypes.includes('fbs') &&
      values.marketplaces.some((item) => item.coverageMode === 'selected' && item.needsDistrictReselection)
    if (needsDistrictReselection) {
      setValue('marketplaces', values.marketplaces)
      setServerError(districtReselectionError)
      showStep(stepOfField('marketplaces'))

      return
    }
    const needsDistricts =
      values.shipmentTypes.includes('fbs') && values.marketplaces.some((item) => item.coverageMode === 'selected')
    // Для безопасного сохранения нужны сами варианты, а не снимок ошибки: данных нет и при ожидании,
    // и когда запрос ещё не успел начаться на восстановленном последнем шаге.
    if (needsDistricts && !federalDistricts) {
      setServerError(districtSaveBlockedError)
      showStep(stepOfField('marketplaces'))

      return
    }
    await validateAndSubmit()
  }

  const goForward = async () => {
    const isStepValid = await trigger(fieldsOfStep(step))
    setWasStepRejected(!isStepValid)
    if (isStepValid) {
      forgetNotices()
      showStep(Math.min(step + 1, formSteps.length - 1))
    }
  }

  const goBack = () => {
    setWasStepRejected(false)
    forgetNotices()
    showStep(Math.max(step - 1, 0))
  }

  const retryCover = async () => {
    if (!savedId) {
      return
    }
    try {
      await uploadCover(savedId)
      await uploadPhotos(savedId)
      finishSuccessfulSave()
    } catch {
      setCoverUploadFailed(true)
      setServerError('Фото снова не загрузилось. Можно продолжить без него.')
    }
  }

  if (mode === 'edit' && !appStore.isLoggedIn) {
    const callback = encodeURIComponent(`/fulfillment/${slug}/edit`)
    return (
      <StatePanel
        title="Нужно войти"
        text="Редактирование доступно владельцу фулфилмента."
        href={`/auth/login?callbackUrl=${callback}`}
        link="Войти"
      />
    )
  }
  if (loadState !== 'ready') {
    const messages = {
      loading: ['Загружаем данные', 'Подождите несколько секунд.'],
      forbidden: ['Нет доступа', 'Редактировать карточку может только её владелец.'],
      'not-found': ['Фулфилмент не найден', 'Проверьте адрес страницы.'],
      error: ['Не удалось загрузить данные', 'Обновите страницу и попробуйте ещё раз.'],
    } as const
    const [title, text] = messages[loadState]
    return <StatePanel title={title} text={text} />
  }

  const isLastStep = step === formSteps.length - 1
  // Плашка держится, пока на шаге правда есть незаполненные поля: исправил — исчезла сама, а не
  // ждёт следующего нажатия «Далее».
  const hasStepError = wasStepRejected && fieldsOfStep(step).some((field) => field in errors)

  return (
    <FormProvider {...form}>
      <form onSubmit={sendForm} noValidate>
        <Card>
          <CardHeader className="border-b">
            <CardTitle>
              {/* Заголовок шага — настоящий h2 и точка, куда уходит фокус при переходе: иначе
                  смена содержимого проходит мимо программы чтения с экрана. Рамку фокуса
                  оставляем видимой — по ней человек с клавиатуры понимает, где он оказался. */}
              <h2
                ref={stepTitle}
                tabIndex={-1}
                className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                Шаг {step + 1} из {formSteps.length}: {formSteps[step].title}
              </h2>
            </CardTitle>
            {mode === 'add' && (
              <CardAction>
                <DraftStatus state={draftAutosave.state} />
              </CardAction>
            )}
          </CardHeader>
          <CardContent className="grid gap-6">
            {step === 0 && <OrganizationStep />}
            {step === 1 && <ShipmentStep />}
            {step === 2 && <GoodsStep />}
            {step === 3 && (
              <PriceStep
                services={services}
                isLoading={isServicesLoading}
                hasLoadError={servicesFailed}
                onRetry={retryServices}
              />
            )}
            {step === 4 && <ContactsStep />}
            {step === 5 && (
              <PresentationStep
                cover={cover}
                slug={slug}
                hasExistingCover={hasExistingCover}
                coverError={coverError}
                onSelectCover={validateCover}
                savedPhotos={savedPhotos}
                photoFiles={photoFiles}
                photoError={photoError}
                onChoosePhotos={choosePhotos}
                onRemoveSavedPhoto={removeSavedPhoto}
              />
            )}

            {notice && (
              <Alert variant="success" role="status">
                <AlertDescription>{notice.text}</AlertDescription>
              </Alert>
            )}

            {hasStepError && (
              <Alert variant="destructive">
                <AlertTitle>Проверьте заполнение</AlertTitle>
                <AlertDescription>Не все поля заполнены верно — они отмечены на этом шаге.</AlertDescription>
              </Alert>
            )}

            {serverError && (
              <Alert variant="destructive">
                <AlertDescription>{serverError}</AlertDescription>
                {coverUploadFailed && savedId && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="destructive" onClick={retryCover}>
                      Повторить загрузку фото
                    </Button>
                    <Button type="button" variant="outline" onClick={finishSuccessfulSave}>
                      Продолжить без фото
                    </Button>
                  </div>
                )}
              </Alert>
            )}
          </CardContent>
          <WizardFooter
            percent={Math.round(((step + 1) / formSteps.length) * 100)}
            nextLabel={nextButtonLabel(isLastStep, isSubmitting, mode)}
            isNextDisabled={isLastStep && isSubmitting}
            onBack={step === 0 ? undefined : goBack}
            onNext={isLastStep ? () => void sendForm() : () => void goForward()}
          />
        </Card>
      </form>
      <FulfillmentCreatedDialog open={isCreatedDialogOpen} onClose={closeDialogAndGoToMyFulfillments} />
    </FormProvider>
  )
})

/** Надпись о черновике и её оформление — одной таблицей, чтобы они не разошлись. */
const draftStatusView: Record<DraftSaveState, { text: string; className: string }> = {
  idle: { text: '', className: '' },
  saving: { text: 'Сохраняем…', className: 'text-muted-foreground' },
  // Заливочный `--success` как цвет мелкого текста даёт около 2,4:1 — берём мягкий, он читается.
  saved: { text: 'Сохранено', className: 'text-success-soft-foreground' },
  'photo-failed': { text: 'Сохранено, кроме фото', className: 'text-warning-soft-foreground' },
  // Заливочный `--destructive` как цвет мелкого текста даёт около 2,9:1 — берём мягкий, там 5,8:1.
  failed: { text: 'Черновик не сохраняется', className: 'text-destructive-soft-foreground' },
}

/**
 * Надпись о судьбе черновика в шапке шага. Область объявления живёт в разметке всегда, даже пустой:
 * программа чтения с экрана сообщает об изменении только внутри уже существующей области.
 *
 * Промежуточное «Сохраняем…» из этой области спрятано: оно появляется на каждую паузу в наборе, и
 * программа чтения с экрана зачитывала бы его после каждого слова, заглушая остальную форму.
 * Роль `status` не берём по простой причине: она уже стоит на плашке «Черновик восстановлен», и
 * поиск по этой роли в тестах начал бы находить два разных сообщения.
 */
const DraftStatus = ({ state }: { state: DraftSaveState }) => {
  const { text, className } = draftStatusView[state]

  return (
    <p aria-live="polite" className={cn(typeClass.supporting, className)}>
      <span aria-hidden={state === 'saving' || undefined}>
        {/* Точка цвета надписи: по ней состояние черновика видно раньше, чем прочитан текст. */}
        {text && (
          <span aria-hidden="true" className="mr-1.5 inline-block size-1.5 rounded-full bg-current align-middle" />
        )}
        {text}
      </span>
    </p>
  )
}

const StatePanel = ({ title, text, href, link }: { title: string; text: string; href?: string; link?: string }) => (
  <Card>
    <CardHeader className="text-center">
      <CardTitle>
        <h2>{title}</h2>
      </CardTitle>
      <CardDescription>{text}</CardDescription>
    </CardHeader>
    {href && (
      <CardContent className="flex justify-center">
        <Button render={<a href={href} />}>{link}</Button>
      </CardContent>
    )}
  </Card>
)
